import { useState } from "react";
import { Card, Input, Button, List, Avatar, Space, Select } from "antd";
import { RobotOutlined, UserOutlined, SendOutlined } from "@ant-design/icons";

const { TextArea } = Input;
const { Option } = Select;

interface Message {
  role: "user" | "assistant";
  content: string;
}

const AIAssistant: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content:
        "¡Hola! Soy tu asistente legal con IA. Puedo ayudarte con análisis de documentos, búsqueda de precedentes, generación de memoriales y más. ¿En qué puedo ayudarte hoy?",
    },
  ]);
  const [input, setInput] = useState("");
  const [assistantType, setAssistantType] = useState("general");
  const [loading, setLoading] = useState(false);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage: Message = { role: "user", content: input };
    setMessages([...messages, userMessage]);
    setInput("");
    setLoading(true);

    // Simulate AI response (in production, this would call the backend API)
    setTimeout(() => {
      const responses: Record<string, string> = {
        general:
          "Basándome en el análisis del caso FUD 701102022500681, puedo observar varios aspectos importantes...",
        precedents:
          "He encontrado los siguientes precedentes relevantes: Auto Supremo 660/2014-RRC sobre allanamiento de domicilio...",
        risk: "Analizando el riesgo del caso, las fortalezas incluyen: documentación sólida, testigos presenciales, informe técnico...",
        memorial:
          "Para el memorial de objeción, recomiendo incluir los siguientes puntos fundamentales...",
      };

      const assistantMessage: Message = {
        role: "assistant",
        content: responses[assistantType] || responses.general,
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setLoading(false);
    }, 1500);
  };

  return (
    <div>
      <h1>🤖 Asistente Comarca con IA</h1>

      <Card style={{ marginBottom: 16 }}>
        <Space>
          <span>Tipo de asistencia:</span>
          <Select
            value={assistantType}
            onChange={setAssistantType}
            style={{ width: 200 }}
          >
            <Option value="general">General</Option>
            <Option value="precedents">Búsqueda de Precedentes</Option>
            <Option value="risk">Análisis de Riesgo</Option>
            <Option value="memorial">Generación de Memorial</Option>
            <Option value="omissions">Detección de Omisiones</Option>
          </Select>
        </Space>
      </Card>

      <Card>
        <div style={{ height: "500px", overflowY: "auto", marginBottom: 16 }}>
          <List
            dataSource={messages}
            renderItem={(message) => (
              <List.Item>
                <List.Item.Meta
                  avatar={
                    message.role === "user" ? (
                      <Avatar icon={<UserOutlined />} />
                    ) : (
                      <Avatar
                        icon={<RobotOutlined />}
                        style={{ backgroundColor: "#1890ff" }}
                      />
                    )
                  }
                  title={message.role === "user" ? "Tú" : "Asistente IA"}
                  description={
                    <div style={{ whiteSpace: "pre-wrap" }}>
                      {message.content}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        </div>

        <Space.Compact style={{ width: "100%" }}>
          <TextArea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Escribe tu pregunta aquí..."
            autoSize={{ minRows: 2, maxRows: 4 }}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSend}
            loading={loading}
            style={{ height: "auto" }}
          >
            Enviar
          </Button>
        </Space.Compact>
      </Card>

      <Card title="💡 Sugerencias" style={{ marginTop: 16 }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Button
            block
            onClick={() =>
              setInput(
                "¿Cuáles son las fortalezas de mi caso FUD 701102022500681?"
              )
            }
          >
            ¿Cuáles son las fortalezas de mi caso?
          </Button>
          <Button
            block
            onClick={() =>
              setInput(
                "Busca precedentes similares a allanamiento de domicilio"
              )
            }
          >
            Buscar precedentes similares
          </Button>
          <Button
            block
            onClick={() =>
              setInput("Genera un memorial de ampliación de denuncia")
            }
          >
            Generar memorial de ampliación
          </Button>
          <Button
            block
            onClick={() =>
              setInput("¿Qué omisiones fiscales detectas en mi caso?")
            }
          >
            Detectar omisiones fiscales
          </Button>
        </Space>
      </Card>
    </div>
  );
};

export default AIAssistant;
